import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addpatientDetails } from "./AppointmentSlice";
import { message } from "antd";
import { getAvailableTimeSlots } from "./Utility";

// Helper function to get fresh headers every time
const getFreshHeaders = () => {
  const token = localStorage.getItem('token');
  // console.log('🔑 GET FRESH HEADERS: Current token:', token ? `${token.substring(0, 20)}...` : 'No token found');
  return {
    Authorization: `Bearer ${token}`,
  };
};

// Helper function for logout
const logoutHandler = (navigate) => {
  console.log('🚪 LOGOUT HANDLER: Clearing localStorage and redirecting');
  localStorage.clear();
  if (navigate) {
    navigate('/');
  } else {
    window.location.href = '/';
  }
};

// Updated networkCall with dynamic token retrieval
export const networkCall = async (method, endpoint, customHeaders = {}, body = null) => {
  try {
    //console.log(`🌐 NETWORK CALL START: ${method} ${endpoint}`);

    // Get fresh headers for every request
    const freshHeaders = getFreshHeaders();
    const requestHeaders = { ...freshHeaders, ...customHeaders };

    console.log(' NETWORK CALL: Request headers Authorization:', requestHeaders.Authorization ? `${requestHeaders.Authorization.substring(0, 30)}...` : 'No Authorization header');

    const options = {
      method,
      headers: requestHeaders,
    };

    if (body) {
      options.body = body;
    }

    const response = await fetch(global.baseUrl + endpoint, options);
    console.log(` NETWORK CALL RESPONSE: Status ${response.status} for ${method} ${endpoint}`);

    // Handle HTTP error codes
    if (response.status === 401) {
      console.error('🚫 NETWORK CALL: 401 Unauthorized detected in response');
      message.error("Unauthorized access detected. Please log in again to continue.");
      logoutHandler();
      return { status: '0', message: "Unauthorized access detected. Please log in again to continue." };
    }

    const data = await response.json();

    // Handle token refresh
    const refreshedToken = data.response_token;
    if (refreshedToken) {
      localStorage.setItem('token', refreshedToken);
      console.log('🔄 Token refreshed successfully:', `${refreshedToken.substring(0, 20)}...`);
    }

    return data;
  } catch (error) {
    // console.error('❌ NETWORK CALL CATCH BLOCK - Error in network call:', error);
    // console.error('❌ NETWORK CALL CATCH BLOCK - Error type:', typeof error);
    // console.error('❌ NETWORK CALL CATCH BLOCK - Error name:', error.name);
    // console.error('❌ NETWORK CALL CATCH BLOCK - Error message:', error.message);
    // console.error('❌ NETWORK CALL CATCH BLOCK - Error stack:', error.stack);

    if (error.response && error.response.status == 401) {
      console.error('🚫 NETWORK CALL CATCH BLOCK - 401 Unauthorized detected');
      message.error(error.response?.data?.message || "Unauthorized access");
      logoutHandler();
    }
    return { status: '0', message: error.message };
  }
};

export const fetchDoctors = createAsyncThunk("api/fetchDoctors", async () => {
  try {
    console.log('👨‍⚕️ FETCH DOCTORS: Starting fetch doctors process');
    const clinicId = localStorage.getItem('clinic_id');
    const user_role = localStorage.getItem('user_role');
    console.log('👨‍⚕️ FETCH DOCTORS: Clinic ID:', clinicId);
    console.log('👨‍⚕️ FETCH DOCTORS: User role:', user_role);

    const response = await networkCall(
      "GET",
      `/api/clinic_mobile_opd/Doctors/clinic_doctors_details?clinic_id=${clinicId}&MDT=CM&user_role=${user_role}`
    );

    console.log('👨‍⚕️ FETCH DOCTORS: Network call response:', response);

    if (response.status == '0') {
      console.error('👨‍⚕️ FETCH DOCTORS: Response status is 0, throwing error:', response.message);
      throw response.message;
    }
    console.log("👨‍⚕️ FETCH DOCTORS: Success response", response);

    const normalizedDoctors = Array.isArray(response.data)
      ? response.data.map((doctor) => ({
        ...doctor,
        id: doctor?.id ?? doctor?.doctor_id,
      }))
      : [];

    return normalizedDoctors;
  } catch (error) {
    // console.error('❌ FETCH DOCTORS CATCH BLOCK - Error occurred:', error);
    // console.error('❌ FETCH DOCTORS CATCH BLOCK - Error type:', typeof error);
    // console.error('❌ FETCH DOCTORS CATCH BLOCK - Error message:', error.message || error);
    throw error;
  }
});

export const bookAppointment = createAsyncThunk(
  "api/bookAppointment",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      console.log('📅 BOOK APPOINTMENT: Starting booking process');
      console.log("📅 BOOK APPOINTMENT: Payload received", payload);

      // Get fresh token before making the request
      const currentToken = localStorage.getItem('token');
      const user_role = localStorage.getItem('user_role');
      console.log('📅 BOOK APPOINTMENT: Current token check:', currentToken ? `${currentToken.substring(0, 20)}...` : 'No token found');
      console.log('📅 BOOK APPOINTMENT: User role:', user_role);

      if (!currentToken) {
        console.error('📅 BOOK APPOINTMENT: No token found in localStorage');
        message.error("Authentication token not found. Please log in again.");
        logoutHandler();
        return rejectWithValue("Authentication token not found");
      }

      let formData = new FormData();
      formData.append("appointment_id", `${payload?.appointment_id ?? ""}`);
      formData.append("clinic_id", "" + localStorage.getItem('clinic_id'));
      formData.append("doctor_id", payload?.doctorId ?? "");
      formData.append("title", `${payload?.title ?? ""}`);
      formData.append("patientName", `${payload?.name ?? ""}`);
      formData.append("patientAge", `${payload?.age ?? ""}`);
      formData.append("patientContactNumber", `${payload?.mobile ?? ""}`);
      formData.append("date", `${payload?.date ?? ""}`);
      formData.append("start_time", `${payload?.start_time ?? ""}`);
      formData.append("end_time", `${payload?.end_time ?? ""}`);
      formData.append("gender", `${payload?.gender ?? ""}`);
      formData.append("email", `${payload?.email ?? ""}`);
      formData.append("cp_id", `${payload?.cp_id ?? ""}`);
      formData.append('MDT', 'CM');
      formData.append('user_role', user_role);
      formData.append('loggedin_user_id', localStorage.getItem('user_id'))

      console.log('📅 BOOK APPOINTMENT: FormData prepared, making network call');

      // Use fresh headers (networkCall will get them dynamically)
      const response = await networkCall(
        "POST",
        "/api/clinic_mobile_opd/Appointments/book",
        {
          // Don't set Authorization here, let networkCall handle it
          processData: false,
          contentType: false
        },
        formData
      );

      // console.log('📅 BOOK APPOINTMENT: Network call response:', response);

      if (response.status == '0') {
        console.error('📅 BOOK APPOINTMENT: Response status is 0:', response.message);
        return rejectWithValue(response.message || "Something went wrong");
      } else if (response.status == '1') {
        console.log('📅 BOOK APPOINTMENT: Success! Dispatching patient details');
        dispatch(
          addpatientDetails({
            userAge: payload?.age,
            name: payload?.name,
            mobile: payload?.mobile,
            title: payload?.title,
          })
        );
        return response.data;
      }
    } catch (error) {
      // console.error('❌ BOOK APPOINTMENT CATCH BLOCK - Error occurred:', error);
      // console.error('❌ BOOK APPOINTMENT CATCH BLOCK - Error type:', typeof error);
      // console.error('❌ BOOK APPOINTMENT CATCH BLOCK - Error name:', error.name);
      // console.error('❌ BOOK APPOINTMENT CATCH BLOCK - Error message:', error.message);
      // console.error('❌ BOOK APPOINTMENT CATCH BLOCK - Error response:', error.response);

      // Check if the error is due to unauthorized access
      if (error.response && (error.response.status === 401)) {
        console.error('🚫 BOOK APPOINTMENT CATCH BLOCK - 401 Unauthorized detected');
        message.error(error.response.message);
        // Clear all localStorage items and redirect to login
        localStorage.clear();
        window.location.href = '/';
        return rejectWithValue(error.response.message);
      }
      return rejectWithValue(error.response?.message || error.message || "Error booking appointment");
    }
  }
);

export const fetchSlots = createAsyncThunk(
  "api/fetchSlots",
  async (payload) => {
    try {
      console.log('🕒 FETCH SLOTS: Starting fetch slots process');
      console.log('🕒 FETCH SLOTS: Payload:', payload);

      const url = `/api/clinic_mobile_opd/Appointments/doctor_weekday_slots?doctor_id=${payload?.id}&dow=${payload?.date}&clinic_id=${payload?.clinicId}&MDT=CM`;
      console.log('🕒 FETCH SLOTS: Request URL:', url);

      const response = await networkCall("GET", url);
      console.log('🕒 FETCH SLOTS: Network call response:', response);

      if (response.status == '0') {
        console.error('🕒 FETCH SLOTS: Response status is 0, throwing error:', response.message);
        throw response.message;
      }

      console.log('🕒 FETCH SLOTS: Success, returning data');
      return response.data;
    } catch (error) {
      // console.error('❌ FETCH SLOTS CATCH BLOCK - Error occurred:', error);
      // console.error('❌ FETCH SLOTS CATCH BLOCK - Error type:', typeof error);
      // console.error('❌ FETCH SLOTS CATCH BLOCK - Error message:', error.message || error);
      throw error;
    }
  }
);

const ApiSlice = createSlice({
  name: "api",
  initialState: {
    loading: false,
    error: null,
    doctors: [],
    slotList: [],
    consultationLength: 0,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDoctors.pending, (state) => {
        console.log('⏳ REDUX: fetchDoctors.pending - Setting loading to true');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        console.error('❌ REDUX: fetchDoctors.rejected - Error:', action.error.message);
        console.error('❌ REDUX: fetchDoctors.rejected - Full action:', action);
        state.loading = false;
        state.error = action.error.message;
        message.error(action.error.message);
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        console.log('✅ REDUX: fetchDoctors.fulfilled - Success with data:', action.payload);
        state.loading = false;
        state.doctors = action.payload;
      });

    builder
      .addCase(fetchSlots.pending, (state) => {
        console.log('⏳ REDUX: fetchSlots.pending - Setting loading to true');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        console.error('❌ REDUX: fetchSlots.rejected - Error:', action.error.message);
        console.error('❌ REDUX: fetchSlots.rejected - Full action:', action);
        state.loading = false;
        state.error = action.error.message;
        message.error(action.error.message);
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        console.log('✅ REDUX: fetchSlots.fulfilled - Success with data:', action.payload);
        state.loading = false;
        const data = action.payload[0];
        state.consultationLength = data?.consultation_length || 0;
        // console.log("🕒 REDUX: fetchSlots.fulfilled - data is ", data);
        // console.log("🕒 REDUX: fetchSlots.fulfilled - start", data?.start_time);
        // console.log("🕒 REDUX: fetchSlots.fulfilled - end", data?.end_time);
        // console.log("🕒 REDUX: fetchSlots.fulfilled - length", data?.consultation_length);

        const slots = getAvailableTimeSlots(
          data?.start_time,
          data?.end_time,
          data?.consultation_length
        );

        if (action.payload.length > 1) {
          console.log('🕒 REDUX: fetchSlots.fulfilled - Processing second slot data');
          const data2 = action.payload[1];
          const slots2 = getAvailableTimeSlots(
            data2?.start_time,
            data2?.end_time,
            data2?.consultation_length
          );

          const slots3 = slots.concat(slots2);
          console.log("🕒 REDUX: fetchSlots.fulfilled - Combined slots3:", slots3);
          state.slotList = slots3;
        }
        else {
          console.log('🕒 REDUX: fetchSlots.fulfilled - Using single slot data');
          state.slotList = slots;
        }

        console.log("🕒 REDUX: fetchSlots.fulfilled - Final state.slotList:", state.slotList);
      });

    builder
      .addCase(bookAppointment.pending, (state) => {
        console.log('⏳ REDUX: bookAppointment.pending - Setting loading to true');
        state.error = null;
        state.loading = true;
      })
      .addCase(bookAppointment.rejected, (state, action) => {
        // console.error('❌ REDUX: bookAppointment.rejected - Error:', action.payload || action.error.message);
        // console.error('❌ REDUX: bookAppointment.rejected - Full action:', action);
        state.loading = false;
        state.error = action.payload || action.error.message;
        message.error(state.error);
        console.log("❌ REDUX: bookAppointment.rejected - Final state.error", action.payload);
      })
      .addCase(bookAppointment.fulfilled, (state, action) => {
        console.log('✅ REDUX: bookAppointment.fulfilled - Success with data:', action.payload);
        state.loading = false;
      });
  },
});

export default ApiSlice.reducer;